import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  Breakdown,
  EventPage,
  FilterOptions,
  IntervalName,
  MetaKeys,
  MetricName,
  Overview,
  PricingRow,
  ServiceAccount,
  ServiceWithKey,
  Timeseries,
  TokenResponse,
  UsageEvent,
} from "@/types/api";
import { type Filters, filtersKey, toApiParams } from "../filters";
import { api } from "./client";

async function get<T>(path: string, params?: URLSearchParams): Promise<T> {
  const { data } = await api.get<T>(path, { params });
  return data;
}

// ---- stats ----

export function useOverview(filters: Filters) {
  return useQuery({
    queryKey: ["overview", filtersKey(filters)],
    queryFn: () => get<Overview>("/v1/stats/overview", toApiParams(filters)),
  });
}

export function useTimeseries(
  filters: Filters,
  metric: MetricName,
  interval: IntervalName,
  groupBy: string | null
) {
  const params = toApiParams(filters);
  params.set("metric", metric);
  params.set("interval", interval);
  if (groupBy) params.set("group_by", groupBy);

  return useQuery({
    queryKey: ["timeseries", filtersKey(filters), metric, interval, groupBy],
    queryFn: () => get<Timeseries>("/v1/stats/timeseries", params),
  });
}

export function useBreakdown(filters: Filters, dimension: string, limit = 50) {
  const params = toApiParams(filters);
  params.set("dimension", dimension);
  params.set("limit", String(limit));

  return useQuery({
    queryKey: ["breakdown", filtersKey(filters), dimension, limit],
    queryFn: () => get<Breakdown>("/v1/stats/breakdown", params),
  });
}

// ---- events ----

export function useEvents(filters: Filters, cursor: string | null, limit = 50) {
  const params = toApiParams(filters);
  params.set("limit", String(limit));
  if (cursor) params.set("cursor", cursor);

  return useQuery({
    queryKey: ["events", filtersKey(filters), cursor, limit],
    queryFn: () => get<EventPage>("/v1/events", params),
    // Keeps the table on screen while the next page loads instead of blanking.
    placeholderData: (previous) => previous,
  });
}

export function useEvent(eventId: string | null) {
  return useQuery({
    queryKey: ["event", eventId],
    queryFn: () => get<UsageEvent>(`/v1/events/${eventId}`),
    enabled: Boolean(eventId),
  });
}

// ---- filter bar options (discovered from the data, not configured) ----

export function useFilterOptions(filters: Filters) {
  return useQuery({
    queryKey: ["filter-options", filtersKey(filters)],
    queryFn: () => get<FilterOptions>("/v1/meta/filters", toApiParams(filters)),
    staleTime: 60_000,
  });
}

export function useMetaKeys(filters: Filters) {
  return useQuery({
    queryKey: ["meta-keys", filtersKey(filters)],
    queryFn: () => get<MetaKeys>("/v1/meta/keys", toApiParams(filters)),
    staleTime: 60_000,
  });
}

// ---- auth ----

export function useLogin() {
  return useMutation({
    mutationFn: async (credentials: { username: string; password: string }) => {
      const { data } = await api.post<TokenResponse>("/v1/auth/login", credentials);
      return data;
    },
  });
}

export function useMe(enabled: boolean) {
  return useQuery({
    queryKey: ["me"],
    queryFn: () => get<{ username: string; created_at: string }>("/v1/auth/me"),
    enabled,
    retry: false,
  });
}

// ---- admin: services / ingest keys ----

export function useServices() {
  return useQuery({
    queryKey: ["services"],
    queryFn: () => get<ServiceAccount[]>("/v1/admin/services"),
  });
}

export function useCreateService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { service_id: string; name: string }) => {
      const { data } = await api.post<ServiceWithKey>("/v1/admin/services", payload);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["services"] }),
  });
}

export function useRotateKey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (serviceId: string) => {
      const { data } = await api.post<ServiceWithKey>(`/v1/admin/services/${serviceId}/rotate-key`);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["services"] }),
  });
}

export function useSetServiceActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceId, active }: { serviceId: string; active: boolean }) => {
      const action = active ? "activate" : "revoke";
      const { data } = await api.post<ServiceAccount>(`/v1/admin/services/${serviceId}/${action}`);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["services"] }),
  });
}

// ---- admin: pricing ----

export function usePricing() {
  return useQuery({
    queryKey: ["pricing"],
    queryFn: () => get<PricingRow[]>("/v1/admin/pricing"),
  });
}

export function useUpsertPricing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      provider: string;
      model: string;
      input_price_per_1k: string;
      output_price_per_1k: string;
      effective_from: string;
    }) => {
      const { data } = await api.put<PricingRow[]>("/v1/admin/pricing", payload);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["pricing"] }),
  });
}

export function useDeletePricing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pricingId: string) => api.delete(`/v1/admin/pricing/${pricingId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["pricing"] }),
  });
}
